<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  card: { type: Object, required: true },
});
const emit = defineEmits(['open']);
const { t } = useI18n();

const status = computed(() =>
  props.card.completed
    ? t('SALES.REVIEW.STATUS.CORRECTED')
    : t('SALES.REVIEW.STATUS.PENDING')
);
const type = computed(() =>
  props.card.kind === 'pj' || props.card.section.endsWith('_pj')
    ? t('SALES.REVIEW.PARTIES.LEGAL_BADGE')
    : t('SALES.REVIEW.PARTIES.PHYSICAL_BADGE')
);
const icon = computed(() => {
  if (props.card.completed) return 'i-lucide-check';
  if (props.card.representative) return 'i-lucide-user-round';
  return props.card.section.endsWith('_pf')
    ? 'i-lucide-user-round'
    : 'i-lucide-building-2';
});
</script>

<template>
  <button
    type="button"
    data-review-party-card
    class="group flex min-h-52 w-full flex-col rounded-lais border bg-white p-4 text-left shadow-lais-soft transition hover:border-lais-primary focus:outline-none focus:ring-2 focus:ring-lais-primary"
    :class="card.completed ? 'border-lais-primary' : 'border-lais-danger'"
    :aria-label="t('SALES.REVIEW.ACTIONS.OPEN')"
    @click="emit('open', card)"
  >
    <span class="flex items-start justify-between gap-3">
      <span class="flex flex-wrap gap-2">
        <span
          class="rounded-full px-2 py-1 text-[11px] font-bold uppercase"
          :class="
            card.completed
              ? 'bg-emerald-50 text-emerald-700'
              : 'bg-red-50 text-red-700'
          "
        >
          {{ status }}
        </span>
        <span
          class="rounded-full bg-lais-bg-soft px-2 py-1 text-[11px] font-bold text-lais-text-muted"
        >
          {{ type }}
        </span>
      </span>
      <span
        v-if="!card.completed"
        class="i-lucide-triangle-alert size-5 text-lais-danger"
        aria-hidden="true"
      />
    </span>

    <span class="grid flex-1 place-items-center gap-3 py-4">
      <span
        class="grid size-14 place-items-center rounded-full"
        :class="
          card.completed
            ? 'bg-emerald-50 text-lais-primary'
            : 'bg-red-50 text-lais-danger'
        "
      >
        <span class="size-7" :class="icon" aria-hidden="true" />
      </span>
      <span class="text-center text-base font-bold text-lais-text">
        {{ card.party.name }}
      </span>
    </span>

    <span
      class="flex items-end justify-between gap-3 text-xs text-lais-text-muted"
    >
      <span>{{ card.subtitle }}</span>
      <span class="i-lucide-arrow-right size-4" aria-hidden="true" />
    </span>
  </button>
</template>
